import { on, reducer } from "ts-action";
import { withLoadingReducer } from "../../../Components/Pickup/redux-store/reducers/withLoadingState";
import { getMultiPosGiftGroupsAction } from "../../../constants";
import * as actions from "../../actions";
import { GiftGroupsState } from "./giftGroupsReducer";
import { GiftItem } from "../../../types";
import { v4 as uuidv4 } from "uuid";
import { notify } from "react-notify-toast";
import {
  createPlaceholderGroup,
  createUpdatedGiftGroupEntries,
  createUpdatedGroupData,
  dispatchUpdateGiftGroupItemsResult,
  isAllGiftGroupsEmpty,
  isPriceOutsideRange,
  isReferencePos,
  syncPlaceholderItem,
  updateExistingGroup,
  validateGroupCompatibility,
} from "./utils";
import {
  initializeGiftGroupsStateByPos,
  getMedianPrice,
  emptyGiftItem,
  removePosGiftGroupItem,
} from "../../../helpers/gift-list";
import { CURRENCIES } from "../../../Utils";
import { translate } from "../../../helpers/translate";
import Strings from "../../../i18n/strings/giftList";
import { isPriceInRange } from "../../../helpers/gift-list/isPriceInRange";

export type MultiPosGiftGroupsState = {
  giftGroupsByPosId: Record<string, GiftGroupsState>;
  selectedPosId?: string;
  posEntities: { name: string; id: string }[];
  currency: {
    en: string;
    ar: string;
  };
  isLoading: boolean;
};

const initialState: MultiPosGiftGroupsState = {
  giftGroupsByPosId: {},
  selectedPosId: undefined,
  posEntities: [],
  currency: {
    en: "",
    ar: "",
  },
  isLoading: false,
};

const t = translate("giftList");

export default withLoadingReducer<MultiPosGiftGroupsState>(
  reducer<MultiPosGiftGroupsState>(
    [
      on(actions.setGiftGroupsCurrency, (state, { payload }) => {
        return {
          ...state,
          currency: {
            en: CURRENCIES["en"][payload.country_iso_code],
            ar: CURRENCIES["ar"][payload.country_iso_code],
          },
        };
      }),
      on(actions.getLoyaltyGiftMenu, (state, { payload }) => {
        return {
          ...state,
          selectedPosId: payload,
        };
      }),
      on(actions.getMultiPosGiftGroupsSuccess, (state, { payload }) => {
        const posEntities = payload.map((pos) => ({
          name: pos?.pos_entity_name,
          id: pos?.pos_entity_id,
        }));
        if (isAllGiftGroupsEmpty(payload)) {
          const group = createPlaceholderGroup({
            name: `Gift - X ${state.currency.en}`,
            ar_name: `هدية - X ${state.currency.ar}`,
            price: 0,
            currency: state.currency,
          });

          const giftGroupsByPosId = Object.fromEntries(
            payload.map((pos) => [
              pos.pos_entity_id,
              {
                ...state.giftGroupsByPosId[pos.pos_entity_id],
                shownGiftGroupsById: { [group.id]: group },
                shownGiftGroupsIds: [group.id],
                giftGroupsIds: [group.id],
                giftGroupsById: { [group.id]: group },
              },
            ])
          );

          return {
            ...state,
            posEntities,
            giftGroupsByPosId,
          };
        }
        return {
          ...state,
          posEntities,
          giftGroupsByPosId: initializeGiftGroupsStateByPos(
            payload,
            state.currency
          ),
        };
      }),
      on(actions.addEmptyMultiPosGiftGroupItem, (state, { payload }) => {
        const { group_id, pos_entity_id } = payload;
        if (!isReferencePos(state, pos_entity_id)) {
          notify.show(
            t(Strings.addItemsToFirstPOS, {
              pos_name: state.posEntities[0].name,
            }),
            "error"
          );
          return state;
        }

        const updatedGiftGroupsByPosId = Object.fromEntries(
          Object.entries(state.giftGroupsByPosId).map(([posId, posState]) => {
            const group = posState.shownGiftGroupsById[group_id];
            if (!group) {
              return [posId, posState];
            }
            const items = group.items as GiftItem[];
            const updatedItems: GiftItem[] = [
              ...items,
              {
                ...emptyGiftItem(items.length + 1),
                show_price: 0,
              },
            ];
            const updatedPrice = getMedianPrice(updatedItems);
            return [
              posId,
              {
                ...posState,
                shownGiftGroupsById: {
                  ...posState.shownGiftGroupsById,
                  [group_id]: {
                    ...group,
                    name: group.is_renamed
                      ? group.name
                      : `Gift - ${updatedPrice} ${state.currency.en}`,
                    ar_name: group.is_ar_renamed
                      ? group.ar_name
                      : `هدية - ${updatedPrice} ${state.currency.ar}`,
                    items: updatedItems,
                    price: updatedPrice,
                  },
                },
              },
            ];
          })
        );

        return {
          ...state,
          giftGroupsByPosId: updatedGiftGroupsByPosId,
        };
      }),
      on(actions.addEmptyMultiPosGiftGroup, (state, { payload }) => {
        const { pos_entity_id } = payload;
        if (!pos_entity_id) return state;
        if (!isReferencePos(state, pos_entity_id)) {
          notify.show(
            t(Strings.addItemsToFirstPOS, {
              pos_name: state.posEntities[0].name,
            }),
            "error"
          );
          return state;
        }

        const id = uuidv4();
        const newGroup = {
          id,
          name: `Gift - X ${state.currency.en}`,
          ar_name: `هدية - X ${state.currency.ar}`,
          is_ar_renamed: 0 as 0 | 1,
          is_renamed: 0 as 0 | 1,
          items: [{ ...emptyGiftItem(1), show_price: 0 }],
          price: 0,
          is_placeholder: 1 as 0 | 1,
        };

        return {
          ...state,
          giftGroupsByPosId: Object.fromEntries(
            Object.entries(state.giftGroupsByPosId).map(([posId, posState]) => {
              return [
                posId,
                {
                  ...posState,
                  shownGiftGroupsById: {
                    ...posState.shownGiftGroupsById,
                    [id]: newGroup,
                  },
                  shownGiftGroupsIds: [...posState.shownGiftGroupsIds, id],
                },
              ];
            })
          ),
        };
      }),
      on(actions.updateMultiPosGiftGroupItems, (state, { payload }) => {
        const { group_id, item, pos_entity_id } = payload;

        if (!pos_entity_id || !state.giftGroupsByPosId[pos_entity_id]) {
          dispatchUpdateGiftGroupItemsResult(item, false);
          return state;
        }

        const currentState = state.giftGroupsByPosId[pos_entity_id];
        const group = currentState.shownGiftGroupsById[group_id];

        if (!item || !group) {
          dispatchUpdateGiftGroupItemsResult(item, false);
          return state;
        }

        const firstItem = group.items[0] as GiftItem;
        const price = Number(firstItem.price || 0);

        if (!isPriceInRange(price, item.price) && !group.is_placeholder) {
          if (!isReferencePos(state, pos_entity_id)) {
            notify.show(
              t(Strings.addItemsToFirstPOS, {
                pos_name: state.posEntities[0].name,
              }),
              "error"
            );
            dispatchUpdateGiftGroupItemsResult(item, false);
            return state;
          }

          const newGroupId = uuidv4();
          const updatedGiftGroupsByPosId = Object.fromEntries(
            createUpdatedGiftGroupEntries(
              state,
              pos_entity_id,
              newGroupId,
              item,
              state.currency
            )
          );

          dispatchUpdateGiftGroupItemsResult(item, true);
          return { ...state, giftGroupsByPosId: updatedGiftGroupsByPosId };
        }

        const updatedItems: GiftItem[] = [...(group.items as GiftItem[])];
        const placeholderIndex = updatedItems.findIndex(
          (i) => i.is_placeholder === 1 && i.price === item.price
        );

        let itemWithOrder: GiftItem;

        if (placeholderIndex !== -1) {
          const placeholderOrder = updatedItems[placeholderIndex].order;
          itemWithOrder = { ...item, order: placeholderOrder };
          updatedItems[placeholderIndex] = itemWithOrder;
        } else {
          itemWithOrder = { ...item, order: updatedItems.length + 1 };
          updatedItems.push(itemWithOrder);
        }

        const compatibilityResult = validateGroupCompatibility(
          state,
          pos_entity_id,
          group_id,
          updatedItems
        );

        if (!compatibilityResult.isValid) {
          notify.show(
            compatibilityResult.errorMessage || "Error updating group",
            "error"
          );
          dispatchUpdateGiftGroupItemsResult(item, false);
          return state;
        }

        if (isReferencePos(state, pos_entity_id)) {
          const updatedGroupData = createUpdatedGroupData(
            group_id,
            updatedItems,
            state.currency
          );
          dispatchUpdateGiftGroupItemsResult(item, true);

          const referenceItemsCount = updatedGroupData?.items?.length ?? 0;

          return {
            ...state,
            giftGroupsByPosId: Object.fromEntries(
              Object.entries(state.giftGroupsByPosId).map(
                ([posId, posState]) => {
                  if (posId === pos_entity_id) {
                    return [
                      posId,
                      {
                        ...posState,
                        shownGiftGroupsById: {
                          ...posState.shownGiftGroupsById,
                          [group_id]: updatedGroupData,
                        },
                      },
                    ];
                  }

                  const currentGroup = posState.shownGiftGroupsById[group_id];
                  if (!currentGroup) {
                    return [posId, posState];
                  }

                  const currentItems = [...(currentGroup?.items ?? [])] as GiftItem[];
                  const placeholdersNeeded = Math.max(0, referenceItemsCount - currentItems.length);

                  const placeholderItems = Array.from({ length: placeholdersNeeded }, (_, index) => ({
                    id: "",
                    is_placeholder: 1 as const,
                    order: itemWithOrder.order,
                    price: itemWithOrder.price,
                    name: "",
                    en_name: "",
                    ar_name: "",
                    show_price: 1 as const,
                  } as GiftItem));

                  const updatedItems = [...currentItems, ...placeholderItems];
                  const updatedPrice = getMedianPrice(updatedItems);

                  return [
                    posId,
                    {
                      ...posState,
                      shownGiftGroupsById: {
                        ...posState.shownGiftGroupsById,
                        [group_id]: {
                          ...currentGroup,
                          items: updatedItems,
                          price: updatedPrice,
                        },
                      },
                    },
                  ];
                }
              )
            ),
          };
        }
        const updatedGroup = updateExistingGroup(
          group,
          updatedItems,
          state.currency
        );

        const updatedGiftGroupsByPosId = {
          ...state.giftGroupsByPosId,
          [pos_entity_id]: {
            ...currentState,
            shownGiftGroupsById: {
              ...currentState.shownGiftGroupsById,
              [group_id]: updatedGroup,
            },
          },
        };

        dispatchUpdateGiftGroupItemsResult(item, true);

        return { ...state, giftGroupsByPosId: updatedGiftGroupsByPosId };
      }),
      on(actions.removeMultiPosGiftGroupItem, (state, { payload }) => {
        const { group_id, item, pos_entity_id } = payload;
        if (!pos_entity_id || !state.giftGroupsByPosId[pos_entity_id])
          return state;

        const currentState = state.giftGroupsByPosId[pos_entity_id];
        const group = currentState.shownGiftGroupsById[group_id];
        const items = group.items as GiftItem[];
        const itemIndex = items.findIndex((i) =>
          item.id ? i.id === item.id : i.order === item.order
        );

        if (itemIndex === -1) return state;

        const updatedGiftGroups = removePosGiftGroupItem({
          state,
          group_id,
          itemIndex,
          currency: state.currency,
          pos_entity_id,
          isReference: isReferencePos(state, pos_entity_id),
        });

        return {
          ...state,
          giftGroupsByPosId: updatedGiftGroups,
        };
      }),
      on(actions.updateMultiPosGiftGroupTitle, (state, { payload }) => {
        const { group_id, name, ar_name, pos_entity_id } = payload;
        if (!pos_entity_id || !state.giftGroupsByPosId[pos_entity_id])
          return state;

        const currentState = state.giftGroupsByPosId[pos_entity_id];
        const group = currentState.shownGiftGroupsById[group_id];
        if (!group) return state;

        return {
          ...state,
          giftGroupsByPosId: {
            ...state.giftGroupsByPosId,
            [pos_entity_id]: {
              ...currentState,
              shownGiftGroupsById: {
                ...currentState.shownGiftGroupsById,
                [group_id]: {
                  ...group,
                  name: name,
                  is_renamed: 1,
                  ar_name: ar_name,
                  is_ar_renamed: 1,
                },
              },
            },
          },
        };
      }),
      on(actions.modifyMultiPosGiftGroups, (state) => ({
        ...state,
        isLoading: true,
      })),
      on(actions.modifyMultiPosGiftGroupsSuccess, (state, { payload }) => {
        return {
          ...state,
          isLoading: false,
          giftGroupsByPosId: Object.fromEntries(
            Object.entries(state.giftGroupsByPosId).map(([posId, posState]) => {
              return [
                posId,
                {
                  ...posState,
                  loading: false,
                  giftGroupsIds: posState.shownGiftGroupsIds,
                  giftGroupsById: posState.shownGiftGroupsById,
                },
              ];
            })
          ),
        };
      }),
      on(actions.modifyMultiPosGiftGroupsFailure, (state) => ({
        ...state,
        isLoading: false,
      })),

      on(actions.addLoyaltyPosGiftItem, (state, { payload }) => {
        if (!payload) return state;
        const newGiftGroupsByPosId = { ...state.giftGroupsByPosId };
        const newGroupId = uuidv4();
        Object.keys(state.giftGroupsByPosId).forEach((posId) => {
          const posState = state.giftGroupsByPosId[posId];

          let matchingGroupId: string | undefined;

          for (const groupId of posState.shownGiftGroupsIds) {
            const group = posState.shownGiftGroupsById[groupId];
            const price = group.price;
            if (isPriceInRange(price, Number(payload.price))) {
              matchingGroupId = groupId;
              break;
            }
          }

          const newItem = {
            id: uuidv4(),
            name: payload.name,
            price: Number(payload.price),
            promo_code: payload.promo_code,
            is_voucher: payload.is_voucher,
            promo_id: payload.promo_id,
            image: payload.image,
            order: payload.order,
            is_special_item: payload.is_special_item,
          };

          if (matchingGroupId) {
            const matchingGroup = posState.shownGiftGroupsById[matchingGroupId];
            const updatedGroupPrice = Math.round(
              getMedianPrice([
                ...(matchingGroup.items as GiftItem[]),
                newItem as unknown as GiftItem,
              ])
            );
            newGiftGroupsByPosId[posId] = {
              ...posState,
              shownGiftGroupsById: {
                ...posState.shownGiftGroupsById,
                [matchingGroupId]: {
                  ...matchingGroup,
                  name: matchingGroup.is_renamed
                    ? matchingGroup.name
                    : `Gift - ${updatedGroupPrice} ${state.currency.en}`,
                  ar_name: matchingGroup.is_ar_renamed
                    ? matchingGroup.ar_name
                    : `هدية - ${updatedGroupPrice} ${state.currency.ar}`,
                  price: updatedGroupPrice,
                  items: [
                    ...(matchingGroup.items as GiftItem[]),
                    {
                      ...(newItem as unknown as GiftItem),
                      order: matchingGroup.items.length + 1,
                    },
                  ],
                },
              },
            };
          } else {
            const newGroup = {
              id: newGroupId,
              name: `Gift - ${Math.round(Number(payload.price))} ${state.currency.en
                }`,
              ar_name: `هدية - ${Math.round(Number(payload.price))} ${state.currency.ar
                }`,
              is_ar_renamed: 0 as 0 | 1,
              is_renamed: 0 as 0 | 1,
              items: [newItem],
              price: Math.round(Number(payload.price)),
            };

            newGiftGroupsByPosId[posId] = {
              ...posState,
              shownGiftGroupsIds: [...posState.shownGiftGroupsIds, newGroupId],
              shownGiftGroupsById: {
                ...posState.shownGiftGroupsById,
                [newGroupId]: newGroup,
              },
            };
          }
        });

        return {
          ...state,
          giftGroupsByPosId: newGiftGroupsByPosId,
        };
      }),
    ],
    initialState
  ),
  getMultiPosGiftGroupsAction
);
